import { Task } from '../types';

export interface MarkdownSection {
  title: string;
  level: number;
  content: string[];
  subsections: MarkdownSection[];
}

export function parseMarkdown(content: string): MarkdownSection[] {
  const lines = content.split('\n');
  const sections: MarkdownSection[] = [];
  const stack: MarkdownSection[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headerMatch = line.match(/^(#+)\s+(.+)/);
    
    if (headerMatch) {
      const level = headerMatch[1].length;
      const title = headerMatch[2];
      
      const section: MarkdownSection = {
        title,
        level,
        content: [],
        subsections: []
      };
      
      // Pop stack until we find parent level
      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop();
      }
      
      // Add to parent or root
      if (stack.length === 0) {
        sections.push(section);
      } else {
        stack[stack.length - 1].subsections.push(section);
      }
      
      stack.push(section);
    } else if (stack.length > 0) {
      stack[stack.length - 1].content.push(line);
    }
  }
  
  return sections;
}

export function parseTasks(content: string): Task[] {
  const lines = content.split('\n');
  const tasks: Task[] = [];
  let currentSection = '';
  let taskId = 1;
  
  for (const line of lines) {
    // Update section
    const headerMatch = line.match(/^#+\s+(.+)/);
    if (headerMatch) {
      currentSection = headerMatch[1];
      continue;
    }
    
    // Parse task
    const taskMatch = line.match(/^-\s*\[([ x])\]\s*(.+)/);
    if (taskMatch) {
      const isCompleted = taskMatch[1] === 'x';
      const content = taskMatch[2];
      
      // Extract priority
      let priority: Task['priority'] = 'medium';
      if (currentSection.toLowerCase().includes('high') || content.includes('🔴')) {
        priority = 'high';
      } else if (currentSection.toLowerCase().includes('low') || content.includes('🟢')) {
        priority = 'low';
      }
      
      // Extract status
      let status: Task['status'] = isCompleted ? 'completed' : 'pending';
      if (currentSection.toLowerCase().includes('progress') || content.includes('🔄')) {
        status = 'in_progress';
      }
      
      tasks.push({
        id: `task-${taskId++}`,
        content: content.replace(/[🔴🟡🟢🔄]/g, '').trim(),
        status,
        priority,
        section: currentSection
      });
    }
  }
  
  return tasks;
}

export function generateMarkdown(sections: MarkdownSection[]): string {
  const lines: string[] = [];
  
  function processSection(section: MarkdownSection) {
    lines.push(`${'#'.repeat(section.level)} ${section.title}`);
    lines.push('');
    
    if (section.content.length > 0) {
      lines.push(...section.content);
      lines.push('');
    }
    
    section.subsections.forEach(processSection);
  }
  
  sections.forEach(processSection);
  
  return lines.join('\n').trim();
}

export function generateTaskList(tasks: Task[], groupBy: 'status' | 'priority' = 'status'): string {
  const lines: string[] = [];
  const now = new Date();
  
  lines.push('# 📋 Task Management');
  lines.push('');
  lines.push(`> Last updated: ${now.toLocaleString()}`);
  lines.push('');
  
  if (groupBy === 'status') {
    // Group by status
    const statuses: Record<Task['status'], { title: string; emoji: string }> = {
      in_progress: { title: 'In Progress', emoji: '🔄' },
      pending: { title: 'Pending', emoji: '📋' },
      completed: { title: 'Completed', emoji: '✅' },
      cancelled: { title: 'Cancelled', emoji: '❌' }
    };
    
    for (const [status, info] of Object.entries(statuses)) {
      const statusTasks = tasks.filter(t => t.status === status);
      if (statusTasks.length > 0) {
        lines.push(`## ${info.emoji} ${info.title} (${statusTasks.length})`);
        lines.push('');
        
        statusTasks.forEach(task => {
          const checkbox = task.status === 'completed' ? '[x]' : '[ ]';
          const priority = task.priority === 'high' ? '🔴' : task.priority === 'low' ? '🟢' : '🟡';
          lines.push(`- ${checkbox} ${priority} ${task.content}`);
        });
        
        lines.push('');
      }
    }
  } else {
    // Group by priority
    const priorities: Record<Task['priority'], { title: string; emoji: string }> = {
      high: { title: 'High Priority', emoji: '🔴' },
      medium: { title: 'Medium Priority', emoji: '🟡' },
      low: { title: 'Low Priority', emoji: '🟢' }
    };
    
    for (const [priority, info] of Object.entries(priorities)) {
      const priorityTasks = tasks.filter(t => t.priority === priority && t.status !== 'completed');
      if (priorityTasks.length > 0) {
        lines.push(`## ${info.emoji} ${info.title} (${priorityTasks.length})`);
        lines.push('');
        
        priorityTasks.forEach(task => {
          const checkbox = task.status === 'completed' ? '[x]' : '[ ]';
          const status = task.status === 'in_progress' ? '🔄' : '';
          lines.push(`- ${checkbox} ${status} ${task.content}`);
        });
        
        lines.push('');
      }
    }
  }
  
  // Add statistics
  const total = tasks.length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  lines.push('## 📊 Statistics');
  lines.push('');
  lines.push(`- **Total Tasks**: ${total}`);
  lines.push(`- **Completed**: ${completed} (${completionRate}%)`);
  lines.push(`- **In Progress**: ${tasks.filter(t => t.status === 'in_progress').length}`);
  lines.push(`- **Pending**: ${tasks.filter(t => t.status === 'pending').length}`);
  
  return lines.join('\n');
}